import { RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FacetCombobox } from '@/components/ui/facet-combobox';
import { PeriodFilter, PeriodFilterOption } from '@/components/ui/period-filter';
import { useI18n } from '@/hooks/useI18n';
import {
    DUE_WINDOW_ALL,
    DUE_WINDOW_OPTIONS,
} from '@/lib/payments/models/payments-dashboard.model';
import {
    IFacetOption,
    PaymentsFacetSelection,
} from '@/lib/payments/models/payments-filters.model';

interface PaymentsFiltersProps {
    search: string;
    onSearchChange: (value: string) => void;
    dueWindowDays: number;
    onDueWindowChange: (days: number) => void;

    selection: PaymentsFacetSelection;
    onSelectionChange: (next: PaymentsFacetSelection) => void;
    itineraryOptions: IFacetOption[];
    /** Assente per il viaggiatore: filtrerebbe su un elenco con il suo solo nome. */
    clientOptions?: IFacetOption[];
    /** Assente per TD e viaggiatore: filtrerebbero su un solo collaboratore, se stessi. */
    designerOptions?: IFacetOption[];

    /** Stato delle tendine quando la lista di opzioni e' parziale. */
    facetQuery: string;
    onFacetQueryChange: (value: string) => void;
    facetsTruncated: boolean;
    facetsSearching: boolean;
    facetsTotalCount: number;

    isRefreshing: boolean;
    onRefresh: () => void;
}

export const PaymentsFilters = ({
    search,
    onSearchChange,
    dueWindowDays,
    onDueWindowChange,
    selection,
    onSelectionChange,
    itineraryOptions,
    clientOptions,
    designerOptions,
    facetQuery,
    onFacetQueryChange,
    facetsTruncated,
    facetsSearching,
    facetsTotalCount,
    isRefreshing,
    onRefresh,
}: PaymentsFiltersProps) => {
    const { t } = useI18n();
    // Basta che il chiamante le passi: sono admin e agenzia, gli unici che vedono piu' di
    // un collaboratore. Nasconderla sotto una certa soglia di opzioni farebbe sparire un
    // filtro atteso solo perche' oggi c'e' un collaboratore solo.
    const showDesignerFilter = designerOptions !== undefined;

    // Le pill portano l'etichetta corta (7g, Tutto); la versione estesa resta a tooltip,
    // perche' "7g" da solo non dice che la finestra guarda in avanti.
    const windowOptions: PeriodFilterOption<number>[] = DUE_WINDOW_OPTIONS.map((days) =>
        days === DUE_WINDOW_ALL
            ? {
                  value: days,
                  label: t('payments.filters.windowAllShort'),
                  title: t('payments.filters.windowAll'),
              }
            : {
                  value: days,
                  label: t('payments.filters.windowShort', { days }),
                  title: t('payments.filters.window', { days }),
              }
    );

    /**
     * Stato della lista di opzioni, comune alle tre tendine.
     *
     * Prima di aver scritto: "mostro i piu' recenti, scrivi per cercare fra tutti" — e' qui
     * che l'utente viene avvisato che la tendina non e' esaustiva, mentre la lista e'
     * ancora piena e nessuno sospetterebbe che manchi qualcosa.
     * Dopo aver scritto, se i risultati sono ancora troppi: "affina la ricerca", invece di
     * lasciar credere che quelli mostrati siano tutti.
     */
    const truncatedLabel = !facetsTruncated
        ? undefined
        : facetQuery.trim()
          ? t('payments.filters.tooManyResults', { count: facetsTotalCount })
          : t('payments.filters.showingRecent', { count: facetsTotalCount });

    // Passare onQueryChange e' cio' che mette la tendina in modalita' ricerca server-side:
    // finche' la lista e' completa resta undefined e cmdk filtra in memoria.
    const serverSearch = facetsTruncated
        ? { query: facetQuery, onQueryChange: onFacetQueryChange }
        : {};

    const shared = {
        ...serverSearch,
        truncated: facetsTruncated,
        truncatedLabel,
        isSearching: facetsSearching,
        searchingLabel: t('payments.filters.searching'),
        clearLabel: t('payments.filters.clear'),
        // Con la ricerca lato server l'assenza di risultati e' una risposta, non un dubbio:
        // il database ha cercato ovunque. Il rimando alla ricerca libera serve perche'
        // quella e' piu' larga (cerca anche sui titoli e sulle email dei collaboratori).
        emptyLabel: t('payments.filters.noOptions'),
        className: 'min-w-[140px]',
    };

    return (
        <div className="flex flex-wrap items-center gap-2">
            <FacetCombobox
                {...shared}
                options={itineraryOptions}
                value={selection.itineraryId}
                onChange={(itineraryId) => onSelectionChange({ ...selection, itineraryId })}
                placeholder={t('payments.filters.allItineraries')}
                searchPlaceholder={t('payments.filters.searchItinerary')}
            />

            {clientOptions && (
                <FacetCombobox
                    {...shared}
                    options={clientOptions}
                    value={selection.clientKey}
                    onChange={(clientKey) => onSelectionChange({ ...selection, clientKey })}
                    placeholder={t('payments.filters.allClients')}
                    searchPlaceholder={t('payments.filters.searchClient')}
                />
            )}

            {showDesignerFilter && (
                <FacetCombobox
                    {...shared}
                    options={designerOptions ?? []}
                    value={selection.designerId}
                    onChange={(designerId) => onSelectionChange({ ...selection, designerId })}
                    placeholder={t('payments.filters.allDesigners')}
                    searchPlaceholder={t('payments.filters.searchDesigner')}
                />
            )}

            {/* Separatore: le tendine filtrano per ID su valori scelti, la ricerca cerca
                testo ovunque. Sono due modi diversi di restringere, e il tratto lo dice
                senza bisogno di un'etichetta. */}
            <span className="w-px h-6 bg-gray-200 mx-1" aria-hidden="true" />

            {/* Dopo le tendine, e piccola: da quando i filtri per ID coprono i casi
                normali questa e' la ricerca trasversale di scorta, il ripiego di chi in
                tendina non trova la voce. Aprire la riga con lei la rimetterebbe al centro. */}
            <div className="relative w-[200px]">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <Input
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder={t('payments.filters.searchPlaceholder')}
                    className="h-9 pl-8 text-sm"
                />
            </div>

            {/* Finestra e refresh a destra: senza piu' un campo elastico che riempie la
                riga, resterebbero appiccicati alle tendine. */}
            <div className="flex items-center gap-2 ml-auto">
                <PeriodFilter
                    options={windowOptions}
                    value={dueWindowDays}
                    onChange={onDueWindowChange}
                    ariaLabel={t('payments.filters.windowLabel')}
                />

                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    aria-label={t('payments.filters.refresh')}
                    className="px-2.5"
                >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </Button>
            </div>
        </div>
    );
};
